import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  PortalLoading,
  SimpleTable,
  StatusBadge,
  TeacherPageHeader,
  useTeacherPortal,
} from "./teacherPortalUtils";

function TeacherClassesPage() {
  const { data, loading, reload } = useTeacherPortal("teacher-portal/classes/");
  const allocationsState = useTeacherPortal("teacher-portal/allocations/");
  const [active, setActive] = useState("students");
  const [selectedClassSubject, setSelectedClassSubject] = useState("");
  const [saving, setSaving] = useState(false);

  const students = data?.students || [];
  const grouped = data?.specialist_allocations || {};
  const allocationData = allocationsState.data || {};
  const allocations = allocationData.allocations || [];
  const available = allocationData.available_class_subjects || [];
  const activeTerm = allocationData.active_term;

  const enrolOptions = useMemo(
    () => available.filter((item) => !item.already_enrolled),
    [available]
  );

  if ((loading && !data) || (allocationsState.loading && !allocationsState.data)) {
    return <PortalLoading label="Loading classes..." />;
  }

  const enrolClass = async (event) => {
    event.preventDefault();
    if (!selectedClassSubject) return toast.warning("Select a class and subject.");

    try {
      setSaving(true);
      await axiosInstance.post("teacher-portal/allocations/", {
        class_subject: selectedClassSubject,
      });
      toast.success("Teaching class added to your portal.");
      setSelectedClassSubject("");
      allocationsState.reload();
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not enrol that teaching class.");
    } finally {
      setSaving(false);
    }
  };

  const removeAllocation = async (allocation) => {
    if (!window.confirm(`Remove ${allocation.class_group_name} - ${allocation.subject_name} from your teaching portal?`)) return;
    try {
      await axiosInstance.delete("teacher-portal/allocations/", {
        data: { allocation: allocation.id },
      });
      toast.success("Teaching class removed from your portal.");
      allocationsState.reload();
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove this teaching class.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <TeacherPageHeader
        title="My Classes"
        subtitle="View your class teacher groups, subject allocations and enrol the classes you teach."
        icon="bi-people"
        actions={<button className="btn btn-outline-success" onClick={() => { reload(); allocationsState.reload(); }}>Refresh</button>}
      />

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="fw-bold mb-1">Enrol a Teaching Class</h5>
          <small className="text-muted">
            Choose an existing class and subject from Academic Setup. This does not create a new class.
          </small>
        </div>
        <div className="card-body">
          <form className="row g-3 align-items-end" onSubmit={enrolClass}>
            <div className="col-lg-8">
              <label className="form-label fw-semibold">Class & Subject</label>
              <select className="form-select" value={selectedClassSubject} onChange={(e) => setSelectedClassSubject(e.target.value)}>
                <option value="">Select the class and subject you teach</option>
                {enrolOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.class_group_name} - {item.subject_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2">
              <div className="small text-muted">Active Term</div>
              <div className="fw-semibold">{activeTerm ? `${activeTerm.academic_year} - ${activeTerm.name}` : "Not configured"}</div>
            </div>
            <div className="col-lg-2 d-grid">
              <button className="btn btn-success" disabled={saving || !selectedClassSubject || !activeTerm}>
                {saving ? "Adding..." : "Add to My Classes"}
              </button>
            </div>
          </form>
          {!activeTerm && <div className="alert alert-warning mt-3 mb-0">Admin must activate a school term before teachers can self-enrol teaching classes.</div>}
        </div>
      </div>

      <div className="d-flex gap-2 mb-3">
        <button className={`btn btn-sm ${active === "students" ? "btn-success" : "btn-light border"}`} onClick={() => setActive("students")}>Students</button>
        <button className={`btn btn-sm ${active === "groups" ? "btn-success" : "btn-light border"}`} onClick={() => setActive("groups")}>Class / Specialist Groups</button>
        <button className={`btn btn-sm ${active === "allocations" ? "btn-success" : "btn-light border"}`} onClick={() => setActive("allocations")}>My Teaching Enrolments</button>
      </div>

      {active === "students" && (
        <SimpleTable
          headers={["Student No.", "Student", "Class", "Status"]}
          rows={students.map((s) => [s.student_number, s.full_name, s.class_group_name, <StatusBadge value={s.status} />])}
        />
      )}

      {active === "groups" && (
        <div className="row g-3">
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm"><div className="card-body">
              <h6 className="fw-bold">Class Teacher Groups</h6>
              {(data?.class_teacher_groups || []).length
                ? data.class_teacher_groups.map((g) => <div className="border rounded-3 p-2 mb-2" key={g.id}>{g.name}<div className="small text-muted">{g.academic_year}</div></div>)
                : <EmptyState title="No class teacher group" />}
            </div></div>
          </div>
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm"><div className="card-body">
              <h6 className="fw-bold">Specialist Allocations</h6>
              {Object.keys(grouped).length
                ? Object.entries(grouped).map(([subject, rows]) => <div key={subject} className="mb-3"><strong>{subject}</strong>{rows.map((r) => <div className="border rounded-3 p-2 mt-2" key={r.id}>{r.class_group_name}<div className="small text-muted">{r.term_name}</div></div>)}</div>)
                : <EmptyState title="No specialist classes" />}
            </div></div>
          </div>
        </div>
      )}

      {active === "allocations" && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white"><h6 className="fw-bold mb-0">My Teaching Enrolments</h6></div>
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="table-light"><tr><th>Class</th><th>Subject</th><th>Term</th><th>Role</th><th></th></tr></thead>
              <tbody>
                {allocations.map((item) => (
                  <tr key={item.id}>
                    <td>{item.class_group_name}</td>
                    <td>{item.subject_name}</td>
                    <td>{item.term_name}</td>
                    <td>{item.is_lead_teacher ? "Lead Teacher" : "Subject Teacher"}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-danger" disabled={item.is_lead_teacher} onClick={() => removeAllocation(item)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {allocations.length === 0 && <tr><td colSpan="5"><EmptyState title="No teaching classes enrolled yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherClassesPage;
